import { useCallback, useEffect, useRef, useState } from "react";

import useToast from "@/contexts/toast-context";

type RecorderState = {
  mediaRecorder: MediaRecorder;
  stream: MediaStream;
  chunks: Blob[];
  mimeType: string;
  extension: string;
};

const pickSupportedMime = (): { mimeType: string; extension: string } | null => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates: Array<{ mimeType: string; extension: string }> = [
    { mimeType: "audio/webm;codecs=opus", extension: "webm" },
    { mimeType: "audio/webm", extension: "webm" },
    { mimeType: "audio/mp4", extension: "mp4" },
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c.mimeType)) return c;
  }
  return null;
};

export type UseAudioRecorderReturn = {
  isRecording: boolean;
  isPaused: boolean;
  durationSec: number;
  start: () => Promise<void>;
  stop: () => Promise<File | null>;
  cancel: () => void;
  pause: () => void;
  resume: () => void;
};

const useAudioRecorder = (): UseAudioRecorderReturn => {
  const { showToast } = useToast();
  const recorderRef = useRef<RecorderState | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [durationSec, setDurationSec] = useState(0);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    clearTimer();
    timerRef.current = setInterval(() => {
      setDurationSec((d) => d + 1);
    }, 1000);
  };

  const cleanupStream = () => {
    const rec = recorderRef.current;
    if (rec) {
      rec.stream.getTracks().forEach((t) => t.stop());
    }
    recorderRef.current = null;
    clearTimer();
    setIsRecording(false);
    setIsPaused(false);
  };

  const start = useCallback(async () => {
    if (recorderRef.current) return;
    const picked = pickSupportedMime();
    if (!picked) {
      showToast({
        text: "Gravação de áudio não suportada neste navegador.",
        type: "error",
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: picked.mimeType });
      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorderRef.current = {
        mediaRecorder,
        stream,
        chunks,
        mimeType: picked.mimeType,
        extension: picked.extension,
      };
      cancelledRef.current = false;
      mediaRecorder.start();
      setDurationSec(0);
      setIsRecording(true);
      setIsPaused(false);
      startTimer();
    } catch {
      showToast({
        text: "Não foi possível acessar o microfone. Verifique as permissões.",
        type: "error",
      });
      cleanupStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stop = useCallback((): Promise<File | null> => {
    return new Promise((resolve) => {
      const rec = recorderRef.current;
      if (!rec) {
        resolve(null);
        return;
      }
      rec.mediaRecorder.onstop = () => {
        const baseMime = rec.mimeType.split(";")[0];
        const wasCancelled = cancelledRef.current;
        const blob = new Blob(rec.chunks, { type: baseMime });
        cleanupStream();
        if (wasCancelled) {
          resolve(null);
          return;
        }
        const file = new File([blob], `audio-${Date.now()}.${rec.extension}`, {
          type: baseMime,
        });
        resolve(file);
      };
      try {
        rec.mediaRecorder.stop();
      } catch {
        cleanupStream();
        resolve(null);
      }
    });
  }, []);

  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec) {
      cleanupStream();
      return;
    }
    cancelledRef.current = true;
    rec.chunks.length = 0;
    rec.mediaRecorder.onstop = () => {
      cleanupStream();
    };
    try {
      if (rec.mediaRecorder.state !== "inactive") {
        rec.mediaRecorder.stop();
      } else {
        cleanupStream();
      }
    } catch {
      cleanupStream();
    }
  }, []);

  const pause = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.mediaRecorder.state !== "recording") return;
    try {
      rec.mediaRecorder.pause();
      clearTimer();
      setIsPaused(true);
    } catch {
      /* noop */
    }
  }, []);

  const resume = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.mediaRecorder.state !== "paused") return;
    try {
      rec.mediaRecorder.resume();
      startTimer();
      setIsPaused(false);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    return () => {
      const rec = recorderRef.current;
      if (rec) {
        cancelledRef.current = true;
        try {
          if (rec.mediaRecorder.state !== "inactive") rec.mediaRecorder.stop();
        } catch {
          /* noop */
        }
        rec.stream.getTracks().forEach((t) => t.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return { isRecording, isPaused, durationSec, start, stop, cancel, pause, resume };
};

export default useAudioRecorder;
